/* eslint-disable import/prefer-default-export */
/* eslint-disable import/exports-last */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { type ReactNode, createContext, useCallback, useContext, useMemo, useReducer, useRef } from 'react'

/* eslint-disable @typescript-eslint/no-unsafe-return */
export function memoize<T extends (...args: any[]) => any>(
  fn: T,
  cmpFn: (a: Parameters<T>, b: Parameters<T>) => boolean = (a, b) => a === b
) {
  let lastArgs: Parameters<T> | null = null
  let lastResult: ReturnType<T> | null = null

  return (...args: Parameters<T>): ReturnType<T> => {
    if (lastArgs && cmpFn(lastArgs, args)) {
      return lastResult as ReturnType<T>
    }
    lastArgs = args
    lastResult = fn(...args)
    return lastResult as ReturnType<T>
  }
}

function useStore<T extends Record<string, unknown>, A extends { type: string }>(
  getInitialState: () => T,
  reducer: (state: T, action: A) => T
) {
  const getInitialStateRef = useRef<() => T>(getInitialState)
  const [state, dispatch_] = useReducer(reducer, getInitialStateRef.current())
  const dispatch = useCallback(
    (action: A) => {
      dispatch_(action)
    },
    [dispatch_]
  )
  return [state, dispatch] as const
}

const StoreCtx = createContext<ReturnType<typeof useStore<any, any>> | null>(null)
const InnerProvider = StoreCtx.Provider
export function StoreProvider<T extends Record<string, unknown>, A extends { type: string }>({
  children,
  getInitialState,
  reducer,
}: {
  children: React.ReactNode
  getInitialState: () => T
  reducer: (state: T, action: A) => T
}) {
  const [state, dispatch] = useStore(getInitialState, reducer)
  return <InnerProvider value={[state, dispatch]}>{children}</InnerProvider>
}

function useStoreContext<T extends Record<string, unknown>, A extends { type: string }>() {
  const store = useContext(StoreCtx)
  if (!store) {
    throw new Error('Store not found')
  }
  return store as ReturnType<typeof useStore<T, A>>
}

export function useSelector<T extends Record<string, unknown>>(selector: (state: T) => any) {
  const memoisedSelector = useMemo(() => memoize(selector), [selector])
  const [state] = useStoreContext<T, any>()
  return memoisedSelector(state)
}

export function useDispatch<A extends { type: string }>() {
  const [, dispatch] = useStoreContext<any, A>()
  return dispatch
}

export type CreateStoreToolsReturn<T extends Record<string, unknown>, A extends { type: string }> = {
  Provider: React.ComponentType<{ children: ReactNode; getInitialState: () => T }>
  useSelector: <S extends (state: T) => any>(selector: S) => ReturnType<S>
  useDispatch: () => (action: A) => void
}

export function createStoreTools<T extends Record<string, unknown>, A extends { type: string }>(
  reducer: (state: T, action: A) => T
): CreateStoreToolsReturn<T, A> {
  return {
    Provider: function Provider({
      children,
      getInitialState,
    }: {
      children: React.ReactNode
      getInitialState: () => T
    }) {
      return (
        <StoreProvider getInitialState={getInitialState} reducer={reducer}>
          {children}
        </StoreProvider>
      )
    },
    useSelector: useSelector<T>,
    useDispatch: useDispatch<A>,
  }
}
